import {
  Fragment,
  HTMLProps,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Fade,
  Theme,
  WithStyles,
  createStyles,
  withStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import {
  StepContext,
  useMultiStepDialogContext,
} from '../MultiStepDialog/context';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      padding: theme.spacing(4),
      minHeight: 0,
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    },
  });

export type StepProps = WithStyles<typeof styles> &
  HTMLProps<HTMLDivElement> & {
    id: string;

    // If the step is optional.
    optional?: boolean;

    // If the step is force reviewed; the user will need to scroll to
    // the bottom of the step before they can proceed to the next step.
    forceReview?: boolean;
  };

const Step = forwardRef<HTMLDivElement, StepProps>((props, ref) => {
  const {
    classes,
    className,
    id,
    optional = false,
    forceReview = false,
    children,
    ...rest
  } = props;
  const { activeStep, steps, addStep } = useMultiStepDialogContext();
  const [seenEverything, setSeenEverything] = useState(false);

  const currentStep = steps[activeStep];
  const isActive = currentStep?.key === id;

  // Register the step with the wizard
  useEffect(() => {
    const removeStep = addStep({ optional, key: id });
    return () => {
      removeStep();
    };
  }, [addStep, id, optional]);

  const contextValue = useMemo(
    () => ({ optional, forceReview, seenEverything, setSeenEverything }),
    [optional, forceReview, seenEverything],
  );

  // We fade in/out the step when active changes.
  // Mount the new step immediately to crossfade without gaps.
  const [inProp, setInProp] = useState(false);
  useEffect(() => {
    setInProp(isActive);
    return undefined;
  }, [isActive]);

  return (
    <Fragment>
      {/* This is a hidden div that is used to determine the order of the steps */}
      <div data-step-id={id} style={{ display: 'none' }} />
      <Fade in={inProp} timeout={120} mountOnEnter unmountOnExit>
        <div
          ref={ref}
          className={classNames(classes.root, className, {
            // Position inactive steps absolutely to avoid layout shifts while crossfading
            [classes.overlay]: !isActive,
          })}
          {...rest}
        >
          <StepContext.Provider value={contextValue}>
            {children}
          </StepContext.Provider>
        </div>
      </Fade>
    </Fragment>
  );
});

Step.displayName = 'MultiStepDialog.Step';

export default withStyles(styles)(Step);
